import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KINDS = [
  { k: "compliment", label: "Compliment", color: "#16a34a" },
  { k: "suggestion", label: "Suggestion", color: "#7c3aed" },
  { k: "complaint", label: "Complaint", color: "#dc2626" },
];

export default function Feedback() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("compliment");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        name: name.trim() || null,
      });
      setSent(true);
      setMessage("");
      setName("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || "Couldn't send feedback — please try again or grab a volunteer",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="flex items-center justify-between border-b-2 border-slate-900 px-5 py-4 sm:px-10">
        <button
          data-testid="feedback-back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#1d4ed8] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Home
        </button>
        <div className="font-heading text-sm tracking-wider text-slate-900">
          GAITHER COWBOYS
        </div>
      </header>

      <main className="mx-auto max-w-xl px-5 py-10 sm:px-10">
        <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-[#1d4ed8]">
          Boosters Feedback
        </div>
        <h1 className="font-heading text-3xl tracking-wider text-slate-900 sm:text-4xl">
          TELL US HOW WE DID
        </h1>
        <p className="mt-3 text-sm text-slate-600">
          Compliment, suggestion, or complaint — the Boosters read every message.
          No order required.
        </p>

        {sent ? (
          <div
            className="mt-8 border-2 border-emerald-600 bg-emerald-50 p-6 text-center"
            data-testid="feedback-sent"
          >
            <div className="font-heading text-xl tracking-wider text-emerald-700">
              THANK YOU!
            </div>
            <div className="mt-2 text-sm text-slate-700">
              Your message reached the Boosters. Go Cowboys!
            </div>
            <div className="mt-4 flex justify-center gap-3">
              <button
                data-testid="feedback-send-another"
                onClick={() => setSent(false)}
                className="border-2 border-slate-900 bg-white px-4 py-2 font-heading text-xs tracking-wider text-slate-900 hover:bg-slate-100"
              >
                SEND ANOTHER
              </button>
              <button
                data-testid="feedback-back-home"
                onClick={() => navigate("/")}
                className="border-2 border-[#1d4ed8] bg-[#1d4ed8] px-4 py-2 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af]"
              >
                BACK TO HOME
              </button>
            </div>
          </div>
        ) : (
          <div
            className="mt-8 space-y-4 border-2 border-slate-900 bg-white p-5"
            data-testid="feedback-form"
          >
            <div className="flex gap-2">
              {KINDS.map((k) => (
                <button
                  key={k.k}
                  data-testid={`feedback-kind-${k.k}`}
                  onClick={() => setKind(k.k)}
                  className={`flex-1 border-2 py-2.5 font-heading text-[11px] tracking-wider ${
                    kind === k.k ? "text-white" : "text-slate-700 bg-white"
                  }`}
                  style={
                    kind === k.k
                      ? { borderColor: k.color, background: k.color }
                      : { borderColor: "#cbd5e1" }
                  }
                >
                  {k.label}
                </button>
              ))}
            </div>

            <input
              data-testid="feedback-name"
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-[#1d4ed8] focus:outline-none"
            />
            <textarea
              data-testid="feedback-message"
              placeholder="Tell us what's on your mind…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={2000}
              className="w-full border border-slate-300 px-3 py-2.5 text-sm focus:border-[#1d4ed8] focus:outline-none"
            />
            <div className="text-right text-[10px] uppercase tracking-[0.2em] text-slate-400">
              {message.length}/2000
            </div>
            <button
              data-testid="feedback-submit"
              onClick={submit}
              disabled={busy || !message.trim()}
              className="w-full border-2 border-[#1d4ed8] bg-[#1d4ed8] py-3 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-50"
            >
              {busy ? "SENDING…" : "SEND MESSAGE"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
